import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { SPORTS, US_STATES } from '../utils/constants';
import MentorCard from '../components/MentorCard';
import './MentorSearchPage.css';

const FETCH_TIMEOUT_MS = 10000;

function MentorSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [sport, setSport] = useState(searchParams.get('sport') || '');
  const [city,  setCity]  = useState(searchParams.get('city')  || '');
  const [state, setState] = useState(searchParams.get('state') || '');

  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {

    let isMounted = true;

    const fetchMentors = async () => {

      try {

        setLoading(true);
        setError('');

        let query = supabase
          .from('profiles')
          .select(`
            id,
            first_name,
            last_name,
            sport,
            secondary_sport,
            focus_areas,
            city,
            state,
            grad_year,
            hourly_rate,
            avatar_url,
            bio,
            tags,
            ratings:ratings!ratings_mentor_id_fkey(rating)
          `)
          .eq('role', 'mentor');

        if (sport) query = query.eq('sport', sport);
        if (city)  query = query.ilike('city', `%${city}%`);
        if (state) query = query.eq('state', state);

        // Timeout guard — without this the spinner hangs forever on a bad connection
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error('Search timed out. Please check your connection and try again.')),
            FETCH_TIMEOUT_MS
          )
        );

        const { data, error: fetchErr } = await Promise.race([
          query.order('first_name'),
          timeoutPromise,
        ]);

        if (fetchErr) throw fetchErr;

        if (!isMounted) return;

        const enriched = (data || []).map((m) => {
          const ratings = m.ratings || [];
          const count   = ratings.length;
          const avg     = count > 0
            ? ratings.reduce((sum, r) => sum + r.rating, 0) / count
            : 0;

          return { ...m, avg_rating: avg, rating_count: count };
        });

        setMentors(enriched);

      } catch (err) {

        if (!isMounted) return;

        console.error('fetchMentors error:', err);
        setError(err.message || 'Failed to load trainers. Please try again.');

      } finally {

        if (isMounted) setLoading(false);

      }

    };

    fetchMentors();

    return () => {
      isMounted = false;
    };

  }, [sport, city, state]);

  const handleFilter = async (e) => {
    e.preventDefault();

    let resolvedCity = city.trim();

    if (/^\d{5}$/.test(resolvedCity)) {
      try {
        const res = await fetch(
          `https://api.zippopotam.us/us/${resolvedCity}`
        );
        if (res.ok) {
          const data = await res.json();
          const placeName = data.places[0]['place name'];
          const placeState = data.places[0]['state abbreviation'];
          resolvedCity = placeName;
          if (!state) setState(placeState);
        }
      } catch (err) {
        console.warn('Zip lookup failed, searching as-is:', err);
      }
    }

    const params = {};
    if (resolvedCity) params.city = resolvedCity;
    if (sport) params.sport = sport;
    if (state) params.state = state;

    setCity(resolvedCity);
    setSearchParams(params);
  };

  const handleClear = () => {
    setSport('');
    setCity('');
    setState('');
    setSearchParams({});
  };

  const hasFilters = sport || city || state;

  return (
    <div className="mentor-search-page">

      {/* Hero */}
      <section className="mentor-search-hero">
        <div className="mentor-search-hero-inner">
          <h1 className="mentor-search-hero-title">
            Find local teen athletes ready to train your child in person.
          </h1>
        </div>
      </section>

      <div className="mentor-search-divider" aria-hidden="true" />

      {/* Filters + Results */}
      <div className="mentor-search-body">

        <form className="mentor-search-filters" onSubmit={handleFilter}>
          <div className="mentor-filter-row">

            <div className="form-group mentor-filter-group">
              <label className="form-label">Sport</label>
              <select
                className="form-select"
                value={sport}
                onChange={(e) => setSport(e.target.value)}
              >
                <option value="">All Sports</option>
                {SPORTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="form-group mentor-filter-group">
              <label className="form-label">City or Zip Code</label>
              <input
                type="text"
                className="form-input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Austin or 49080"
              />
            </div>

            <div className="form-group mentor-filter-group">
              <label className="form-label">State</label>
              <select
                className="form-select"
                value={state}
                onChange={(e) => setState(e.target.value)}
              >
                <option value="">All States</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="mentor-filter-actions">
              <button type="submit" className="btn btn-primary">
                Search
              </button>
              {hasFilters && (
                <button type="button" className="btn btn-ghost" onClick={handleClear}>
                  Clear
                </button>
              )}
            </div>

          </div>
        </form>

        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div className="app-loading">
            <div className="app-loading-spinner" />
          </div>
        ) : mentors.length === 0 ? (
          <div className="mentor-search-empty">
            <p>No trainers found{hasFilters ? ' for your search' : ''}.</p>
            {hasFilters && (
              <button className="btn btn-ghost" onClick={handleClear}>
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="mentor-search-count">
              {mentors.length} trainer{mentors.length !== 1 ? 's' : ''} found
            </p>
            <div className="mentor-card-grid">
              {mentors.map((m) => (
                <MentorCard key={m.id} mentor={m} />
              ))}
            </div>
          </>
        )}

      </div>
    </div>
  );
}

export default MentorSearchPage;
